import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AdminReportDetail,
  AdminReportListItem,
  Paginated,
  ReportStatus,
  ReportableType,
} from "@/types";

export interface ReportsFilters {
  status?: ReportStatus;
  reportableType?: ReportableType;
  search?: string;
  cursor?: string;
  limit?: number;
}

export function useReports(filters: ReportsFilters = {}) {
  return useQuery({
    queryKey: ["reports", filters],
    queryFn: () => {
      const params: Record<string, string> = {
        limit: String(filters.limit ?? 25),
      };
      if (filters.status) params.status = filters.status;
      if (filters.reportableType) params.reportableType = filters.reportableType;
      if (filters.search) params.search = filters.search;
      if (filters.cursor) params.cursor = filters.cursor;
      return api.get<Paginated<AdminReportListItem>>("/admin/reports", params);
    },
  });
}

export function useReport(id: string | null) {
  return useQuery({
    queryKey: ["report", id],
    queryFn: () => api.get<AdminReportDetail>(`/admin/reports/${id ?? ""}`),
    enabled: id !== null,
  });
}

export function useResolveReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, adminReply }: { id: string; adminReply: string }) =>
      api.patch<AdminReportDetail>(`/admin/reports/${id}/resolve`, { adminReply }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
      void queryClient.invalidateQueries({ queryKey: ["report", id] });
    },
  });
}
